import { createEventBus } from "@core/features/eventBus";
import type { QuizQuestionAnswer } from "./quiz.types";

/**
 * Where a quiz was being answered, for whatever measures it.
 *
 * A quiz does not know what it is part of. Whatever puts it on screen does,
 * and says so here: the intake form it belongs to, the page it was reached
 * from. It travels with every event so that a report can be narrowed to one
 * setting without the quiz feature having to know the settings exist.
 */
export type QuizEventContext = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * The stages of answering a quiz, in the order somebody passes through them:
 * it was put in front of them, they began, they reached each of its screens,
 * they passed the middle, and they finished or were stopped.
 */
export type QuizEvents = {
  /** The quiz was put on screen */
  "quiz:loaded": { quizId: number; context?: QuizEventContext };

  /** The first answer or choice was made */
  "quiz:started": { quizId: number | string; context?: QuizEventContext };

  /** One screen of a quiz asked over several was reached */
  "quiz:step": {
    quizId: number;
    /** Counted from one */
    step: number;
    stepCount: number;
    /** What the screen asks for, such as contact details or questions */
    stepKind: string;
    context?: QuizEventContext;
  };

  /** More than half of the quiz's screens have been reached */
  "quiz:halfway": { quizId: number; context?: QuizEventContext };

  "quiz:answer:submitted": {
    questionId: number;
    answer: QuizQuestionAnswer;
    quizId: number;
    responseId: number;
  };

  /** Finishing the quiz failed, with why */
  "quiz:error": { quizId: number; reason: string; context?: QuizEventContext };

  /**
   * Something on a screen stopped somebody short of sending, with why: what
   * was offered could not be read, there was nothing to offer, or answers
   * were refused when they tried to move on. Kept apart from `quiz:error`,
   * which is a submission that failed, so that a count of those stays one.
   */
  "quiz:problem": {
    quizId: number;
    reason: string;
    /** What the screen asks for, such as a day or contact details */
    stepKind?: string;
    /** The fields that were refused, by name and never by what was in them */
    fields?: string[];
    context?: QuizEventContext;
  };

  "quiz:completed": {
    quizId: number;
    invitationId?: number;
    /** The response the answers were stored as, once there is one */
    responseId?: number | null;
    context?: QuizEventContext;
  };
};

export const quizEventBus = createEventBus<QuizEvents>();
